<template>
  <div>
    <div class="box">
      <h3 class="title is-5">Goals and Demographics</h3>
      <div class="field">
        <label class="label">Current Age</label>
        <div class="control">
          <input class="input" type="number" v-model="clientGoals.current_age" placeholder="Enter your current age" />
        </div>
      </div>
      <div class="field">
        <label class="label">Age to Retire</label>
        <div class="control">
          <input class="input" type="number" v-model="clientGoals.retirement_age" placeholder="Enter Age to Retire" />
        </div>
      </div>
      <div class="field">
        <label class="label">Annual Retirement Income Goal</label>
        <div class="control">
          <input class="input" type="number" v-model="clientGoals.retirement_income" placeholder="Enter Annual Retirement Income Goal" />
        </div>
      </div>
      <div class="field">
        <label class="label">Annual Retirement Contribution</label>
        <div class="control">
          <input class="input" type="number" v-model="clientGoals.annual_retirement_contribution" placeholder="Enter your annual retirement contribution" />
        </div>
      </div>
      <div class="field">
        <label class="label">Expected Annual Growth Rate (%)</label>
        <div class="control">
          <input class="input" type="number" v-model="clientGoals.annual_growth_rate" placeholder="Enter the expected annual growth rate" />
        </div>
      </div>
      <div class="field">
        <label class="label">Risk Tolerance</label>
        <div class="control">
          <input class="input" type="text" v-model="clientGoals.risk_tolerance" placeholder="Enter Risk Tolerance" />
        </div>
      </div>
      <div class="field">
        <label class="label">Liquid Assets</label>
        <div class="control">
          <input class="input" type="number" v-model="clientGoals.liquid_assets" placeholder="Calculated Liquid Assets" />
        </div>
      </div>
      <div class="field">
        <label class="label">Total Assets</label>
        <div class="control">
          <input class="input" type="number" v-model="clientGoals.total_assets" placeholder="Enter Total Assets" />
        </div>
      </div>

      <!-- Submit Button -->
      <div class="box">
        <button class="button is-primary" @click="generateFinancialPlan">Submit</button>
      </div>
    </div>

    <div v-if="showGeneratedPlan" class="section">
      <div class="container">
        <div class="box">
          <h3 class="title is-4">Introduction</h3>
          <p>Welcome to your personalized financial plan. This report is designed to give you a comprehensive overview of your financial situation and help you make informed decisions to achieve your financial goals.</p>
        </div>

        <!-- Goals and Demographics -->
        <div class="box">
          <h3 class="title is-4">Goals and Demographics</h3>
          <p>Based on the information you provided, here are your key financial goals and personal details:</p>
          <p><strong>Current Age:</strong> {{ clientGoals.current_age }}</p>
          <p><strong>Age to Retire:</strong> {{ clientGoals.retirement_age }}</p>
          <p><strong>Annual Retirement Income Goal:</strong> {{ clientGoals.retirement_income }}</p>
          <p><strong>Risk Tolerance:</strong> {{ clientGoals.risk_tolerance }}</p>
          <p><strong>Liquid Assets:</strong> {{ clientGoals.liquid_assets }}</p>
          <p><strong>Total Assets:</strong> {{ clientGoals.total_assets }}</p>
        </div>

        <!-- Asset Breakdown -->
        <div class="box">
          <h3 class="title is-4">Asset Breakdown</h3>
          <p>Your assets are distributed across the following accounts:</p>
          <ul>
            <li v-for="account in filteredBankAccounts" :key="account.name">
              <strong>{{ account.name }}</strong>: {{ account.balance }}
            </li>
            <li v-for="investment in filteredInvestments" :key="investment.account_name">
              <strong>{{ investment.account_name }}</strong>: {{ investment.balance }}
            </li>
          </ul>
          <div class="canvas-container">
            <AssetBreakdownChart :data="assetBreakdownData" />
          </div>
        </div>

        <!-- Retirement Projection -->
        <div class="box">
          <h3 class="title is-4">Retirement Projection</h3>
          <p>Based on your current savings, annual contributions, and expected growth rate, here is the projection of your retirement savings over time:</p>
          <div class="canvas-container">
            <RetirementProjectionChart :data="retirementProjectionData" />
          </div>
        </div>

        <!-- Probability of Success -->
        <div class="box">
          <h3 class="title is-4">Probability of Success</h3>
          <p>The probability of successfully achieving your retirement income goal based on the current plan is:</p>
          <p>{{ probabilityOfSuccess }}%</p>
        </div>

        <!-- Tax Information -->
        <div v-if="generatedPlan.taxInformation.length" class="box">
          <h3 class="title is-4">Tax Information</h3>
          <p>Here is a summary of your tax information:</p>
          <div v-for="tax in generatedPlan.taxInformation" :key="tax.type">
            <p><strong>Taxpayer Name:</strong> {{ tax.taxpayer_name }}</p>
            <p><strong>Filing Status:</strong> {{ tax.filing_status }}</p>
            <p><strong>Total Income:</strong> {{ tax.total_income }}</p>
            <p><strong>Tax Owed:</strong> {{ tax.tax_owed }}</p>
          </div>
        </div>

        <!-- Estate Planning -->
        <div v-if="showEstateInfo" class="box">
          <h3 class="title is-4">Estate Planning</h3>
          <p>Estate planning is a crucial part of your financial plan. Here is the information we have:</p>
          <div v-if="estateInfo.health_proxy.agent_name">
            <h5 class="title is-5">Health Proxy</h5>
            <p v-if="estateInfo.health_proxy.agent_name"><strong>Agent Name:</strong> {{ estateInfo.health_proxy.agent_name }}</p>
            <p v-if="estateInfo.health_proxy.agent_contact"><strong>Agent Contact:</strong> {{ estateInfo.health_proxy.agent_contact }}</p>
            <p v-if="estateInfo.health_proxy.alternate_agent_name"><strong>Alternate Agent Name:</strong> {{ estateInfo.health_proxy.alternate_agent_name }}</p>
            <p v-if="estateInfo.health_proxy.alternate_agent_contact"><strong>Alternate Agent Contact:</strong> {{ estateInfo.health_proxy.alternate_agent_contact }}</p>
            <p v-if="estateInfo.health_proxy.effective_date"><strong>Effective Date:</strong> {{ estateInfo.health_proxy.effective_date }}</p>
            <p v-if="estateInfo.health_proxy.instructions"><strong>Instructions:</strong> {{ estateInfo.health_proxy.instructions }}</p>
          </div>

          <div v-if="estateInfo.will.executor_name">
            <h5 class="title is-5">Will</h5>
            <p v-if="estateInfo.will.testator_name"><strong>Testator Name:</strong> {{ estateInfo.will.testator_name }}</p>
            <p v-if="estateInfo.will.executor_name"><strong>Executor Name:</strong> {{ estateInfo.will.executor_name }}</p>
            <p v-if="estateInfo.will.beneficiaries"><strong>Beneficiaries:</strong> {{ estateInfo.will.beneficiaries }}</p>
            <p v-if="estateInfo.will.assets"><strong>Assets:</strong> {{ estateInfo.will.assets }}</p>
            <p v-if="estateInfo.will.date_of_will"><strong>Date of Will:</strong> {{ estateInfo.will.date_of_will }}</p>
            <p v-if="estateInfo.will.witnesses"><strong>Witnesses:</strong> {{ estateInfo.will.witnesses }}</p>
          </div>

          <div v-if="estateInfo.power_of_attorney.principal_name">
            <h5 class="title is-5">Power of Attorney</h5>
            <p v-if="estateInfo.power_of_attorney.principal_name"><strong>Principal Name:</strong> {{ estateInfo.power_of_attorney.principal_name }}</p>
            <p v-if="estateInfo.power_of_attorney.agent_name"><strong>Agent Name:</strong> {{ estateInfo.power_of_attorney.agent_name }}</p>
            <p v-if="estateInfo.power_of_attorney.agent_contact"><strong>Agent Contact:</strong> {{ estateInfo.power_of_attorney.agent_contact }}</p>
            <p v-if="estateInfo.power_of_attorney.powers_granted"><strong>Powers Granted:</strong> {{ estateInfo.power_of_attorney.powers_granted }}</p>
            <p v-if="estateInfo.power_of_attorney.effective_date"><strong>Effective Date:</strong> {{ estateInfo.power_of_attorney.effective_date }}</p>
            <p v-if="estateInfo.power_of_attorney.expiration_date"><strong>Expiration Date:</strong> {{ estateInfo.power_of_attorney.expiration_date }}</p>
          </div>
        </div>

        <!-- Insights Section -->
        <div class="insights-section" v-if="insights.length">
          <h3 class="title is-4">Actionable Insights</h3>
          <p>Based on the analysis, here are some actionable insights to help you achieve your financial goals:</p>
          <div v-for="insight in insights" :key="insight.title" class="insight">
            <h4 class="title is-5">{{ insight.title }}</h4>
            <p>{{ insight.message }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AssetBreakdownChart from '../components/DocmasterAssetAllocation.vue';
import RetirementProjectionChart from '../components/DocmasterRetirementProjectionChart.vue';

export default {
  name: 'FinancialPlan',
  components: { 
    AssetBreakdownChart,
    RetirementProjectionChart
  },
  props: {
    showEstateInfo: Boolean,
    estateInfo: Object,
    showGoalsForm: Boolean,
    investmentInfo: {
      type: Array,
      default: () => []
    },
    bankStatementInfo: {
      type: Array,
      default: () => []
    },
    taxReturnInfo: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      clientGoals: {
        retirement_age: 65,
        retirement_income: 100000,
        risk_tolerance: "Aggressive",
        liquid_assets: 300000,
        total_assets: 500000,
        current_age: 30,
        annual_retirement_contribution: 3000,
        annual_growth_rate: 8,
      },
      generatedPlan: null,
      showGeneratedPlan: false,
      assetBreakdownData: {},
      retirementProjectionData: [],
      probabilityOfSuccess: null,
      numberOfSimulations: 1000, // Default number of simulations for Monte Carlo
      expectedReturn: 0.07, // Example expected return (7%)
      standardDeviation: 0.15, // Example standard deviation (15%)
      insights: []
    };
  },
  computed: {
    filteredBankAccounts() {
      return this.generatedPlan.assets.bankAccounts.filter(
        account => account.name && parseFloat(account.balance) > 0
      );
    },
    filteredInvestments() {
      return this.generatedPlan.assets.investments.filter(
        investment => investment.account_name && parseFloat(investment.balance) > 0
      );
    },
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
      };
    },
  },
  methods: {
    generateFinancialPlan() {
      const financialPlanData = {
        clientGoals: this.clientGoals,
        bankStatementInfo: this.bankStatementInfo,
        investmentInfo: this.investmentInfo,
        taxReturnInfo: this.taxReturnInfo,
        estateInfo: this.estateInfo
      };

      this.calculateRetirementProjection();
      this.calculateProbabilityOfSuccess();

      console.log('Generating financial plan with the following data:', financialPlanData);

      // Send data to the backend
      this.sendFinancialPlanToBackend(financialPlanData);
    },
    calculateRetirementProjection() {
      const projectionData = [];
      const yearsToRetirement = this.clientGoals.retirement_age - this.clientGoals.current_age;
      let currentSavings = parseFloat(this.clientGoals.liquid_assets);
      const annualContribution = parseFloat(this.clientGoals.annual_retirement_contribution);
      const growthRate = parseFloat(this.clientGoals.annual_growth_rate) / 100;

      for (let i = 0; i <= yearsToRetirement; i++) {
        projectionData.push({
          year: new Date().getFullYear() + i,
          savings: currentSavings
        });
        currentSavings = currentSavings * (1 + growthRate) + annualContribution;
      }

      this.retirementProjectionData = projectionData;
    },
    calculateProbabilityOfSuccess() {
      const simulations = [];
      const { current_age, retirement_age, annual_retirement_contribution, retirement_income, liquid_assets } = this.clientGoals;
      const yearsToRetirement = retirement_age - current_age;
      const annualReturn = this.expectedReturn / 100;
      const stdDev = this.standardDeviation / 100;

      for (let i = 0; i < this.numberOfSimulations; i++) {
        let portfolioValue = parseFloat(liquid_assets);

        for (let year = 0; year < yearsToRetirement; year++) {
          const randomReturn = this.randomNormalDistribution(annualReturn, stdDev);
          portfolioValue = portfolioValue * (1 + randomReturn) + parseFloat(annual_retirement_contribution);
        }

        simulations.push(portfolioValue);
      }

      const successfulSimulations = simulations.filter(value => value >= retirement_income * (retirement_age - current_age)).length;
      this.probabilityOfSuccess = (successfulSimulations / this.numberOfSimulations) * 100;
    },
    randomNormalDistribution(mean, stdDev) {
      let u1 = 0, u2 = 0;
      while (u1 === 0) u1 = Math.random(); // Convert [0,1) to (0,1)
      while (u2 === 0) u2 = Math.random();
      const randStdNormal = Math.sqrt(-2.0 * Math.log(u1)) * Math.sin(2.0 * Math.PI * u2);
      return mean + stdDev * randStdNormal;
    },
    async sendFinancialPlanToBackend(financialPlanData) {
      console.log('FINANCIAL PLAN DATA HERE')
      console.log(financialPlanData)
      const url = '/api/v1/generate_financial_plan/';
      const headers = {
        'Content-Type': 'application/json',
        'X-CSRFToken': this.$root.$data.csrfToken,
      };

      try {
        const res = await fetch(url, {
          method: 'POST',
          headers: headers,
          body: JSON.stringify(financialPlanData),
        });
        const result = await res.json();
        console.log('Response from backend:', result);
        // Display the financial plan to the user
        this.displayFinancialPlan(result);
      } catch (error) {
        console.error('Error generating financial plan:', error);
      }
    },
    generateInsights(plan) {
      const insights = [];

      // Insight: Check if the client has a will
      if (!plan.estatePlanning.will || !plan.estatePlanning.will.testator_name) {
        insights.push({
          title: "Create a Will",
          message: "You do not have a will. It's important to have a will to ensure your assets are distributed according to your wishes."
        });
      }

      // Insight: Check if the client has a health proxy
      if (!plan.estatePlanning.health_proxy || !plan.estatePlanning.health_proxy.agent_name) {
        insights.push({
          title: "Designate a Health Proxy",
          message: "You do not have a designated health proxy. A health proxy is crucial to ensure your medical decisions are made by someone you trust in case you're unable to make them yourself."
        });
      }

      // Insight: Check if the client has a power of attorney
      if (!plan.estatePlanning.power_of_attorney || !plan.estatePlanning.power_of_attorney.principal_name) {
        insights.push({
          title: "Designate a Power of Attorney",
          message: "You do not have a power of attorney document. A power of attorney allows you to appoint someone to manage your financial affairs if you're unable to do so."
        });
      }

      // Add more insights based on other aspects of the financial plan...

      return insights;
    },
    calculateAssetBreakdown(assets) {
      const breakdown = {};

      assets.bankAccounts.forEach(account => {
        if (account.name && parseFloat(account.balance) > 0) {
          breakdown[account.name] = parseFloat(account.balance);
        }
      });

      assets.investments.forEach(investment => {
        if (investment.account_name && parseFloat(investment.balance) > 0) {
          breakdown[investment.account_name] = parseFloat(investment.balance);
        }
      });

      return breakdown;
    },
    displayFinancialPlan(plan) {
      this.generatedPlan = plan;
      console.log('Generated Plan:', this.generatedPlan);
      this.insights = this.generateInsights(plan);
      this.assetBreakdownData = this.calculateAssetBreakdown(plan.assets);
      this.showGeneratedPlan = true;
    }
  }
};
</script>

<style scoped>
.box {
  background-color: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
}

.title, .subtitle {
  color: #4a4a4a;
}

.canvas-container {
  max-width: 100%;
  max-height: 400px;
  margin-bottom: 20px;
}

.insights-section .insight {
  background: #f9f9f9;
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.insights-section .insight h4 {
  margin-bottom: 5px;
}
</style>
